import React, { useRef, useState, useEffect, useContext } from 'react';
import { Button, Table, Modal, Form, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { CartContext } from '../../../../context/CartContext';
import { Is_Login } from '../../../../helper/IsLogin';
import { Rating } from '@mui/material';
import api from '../../../../helper/apiAdmin';
import { getServerURL } from '../../../../helper/envConfig';
import DynamicPagination from '../DynamicPagination';
import Layout from '../layout/Layout';
import deletereview from '../../../admin/page/assets/img/deleteuserreview.png';
import editicon from '../../../admin/page/assets/img/edit.png';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const ListUserReview = () => {
    const { id } = useParams();
    const product_id = id;
    const isLoggedIn = Is_Login();
    const navigate = useNavigate();
    const { setMainLoder } = useContext(CartContext);
    const [reviewList, setReviewList] = useState([]);
    const [page, setPage] = useState(1);
    const serverURL = getServerURL();
    const [loading, setLoading] = useState(true);
    const player = useRef();

    // Edit functionality
    const [showEditModal, setShowEditModal] = useState(false);
    const [editReview, setEditReview] = useState({
        _id: '',
        title: '',
        reviewer_display_name: '',
        source_reviewer_name: '',
        content: '',
        heading: '',
        rating: 0,
        review_files: [],
        review_type: 0
    });
    
    // File handling states
    const [filesToRemove, setFilesToRemove] = useState([]);
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const startAnimation = () => {
        if (player.current) {
            player.current.play();
        }
    };

    const stopAnimation = () => {
        setLoading(false);
    };

    const itemsPerPage = 10;

    const getProductReview = async () => {
        startAnimation();
        setLoading(true);
        try {
            const apiTyp = isLoggedIn ? api.postWithToken : api.post;
            if (product_id) {
                const productReview = await apiTyp(`${serverURL}api/product-review-list/`, {
                    product_id: product_id, page: page, limit: itemsPerPage, 
                });
                setReviewList(productReview.data.data || { list: [], totalPages: 0, currentPage: 1 });
                stopAnimation();
            }
        } catch (error) {
            console.log(error);
            setReviewList({ list: [], totalPages: 0, currentPage: 1 });
            stopAnimation();
        }
    };
    
    useEffect(() => {
        getProductReview();
    }, [product_id, isLoggedIn, page]);

    const deleteReview = async (reviewId) => {
        setMainLoder(true);
        try {
            await api.post(`${serverURL}api/delete-review`, { review_id: reviewId });
            if (reviewList.list.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                startAnimation();
                setLoading(true);
                const apiTyp = isLoggedIn ? api.postWithToken : api.post;
                const productReview = await apiTyp(`${serverURL}api/product-review-list/`, {
                    product_id: product_id,
                    page: page,
                    limit: itemsPerPage,
                });
                setReviewList(productReview.data.data || { list: [], totalPages: 0, currentPage: 1 });
                stopAnimation();
            }
        } catch (error) {
            console.log(error);
        } finally {
            setMainLoder(false);
        }
    };

    // Handle opening the edit modal
    const handleEditClick = (review) => {
        setEditReview({
            _id: review._id,
            title: review.title || '',
            reviewer_display_name: review.reviewer_display_name || review.source_reviewer_name || review.user_id?.name || review.title || "Shopper",
            source_reviewer_name: review.source_reviewer_name || '',
            content: review.content,
            rating: review.rating,
            heading: review.heading,
            review_files: review.review_files,
            review_type: review.review_type || 0,
            fileNames: [],
            newFiles: []
        });
        setFilesToRemove([]);
        setShowEditModal(true);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditReview(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle rating change
    const handleRatingChange = (event, newValue) => {
        setEditReview(prev => ({
            ...prev,
            rating: newValue
        }));
    };
    
    // Handle file selection - using the working logic from CreateUserReview
    const handlePhoto = (e) => {
        const files = e.target.files;
        const fileNames = Array.from(files).map(file => file.name);
        
        // Check if total files selected exceeds the limit
        if (fileNames.length > 5) {
            handleErrorSnackbar("You can only upload a maximum of 5 files. Please select fewer files.");
            // Clear the input field
            e.target.value = null;
            return;
        }

        setEditReview(prev => ({
            ...prev,
            newFiles: files,
            fileNames: fileNames
        }));
    };
    
    // Mark existing file to be removed
    const handleRemoveExistingFile = (fileId) => {
        setFilesToRemove(prev => [...prev, fileId]);
    };
    
    // Restore a file marked for removal
    const handleRestoreFile = (fileId) => {
        setFilesToRemove(prev => prev.filter(id => id !== fileId));
    };

    // Submit edited review
    const submitEditedReview = async (e) => {
        e.preventDefault();
        setMainLoder(true);
        
        try {
            // Create form data to handle file uploads using the working approach from CreateUserReview
            const formData = new FormData();
            formData.append('action', 'update');
            formData.append('_id', editReview._id);
            formData.append('title', editReview.title);
            formData.append('reviewer_display_name', editReview.reviewer_display_name);
            formData.append('heading', editReview.heading);
            formData.append('content', editReview.content);
            formData.append('rating', editReview.rating);
            
            // Add files to remove if any
            if (filesToRemove.length > 0) {
                formData.append('files_to_remove', JSON.stringify(filesToRemove));
            }
            
            // Handle file uploads and review type similar to CreateUserReview
            if (editReview.newFiles && editReview.newFiles.length > 0) {
                let isImage = false;
                let isVideo = false;
                
                for (let i = 0; i < editReview.newFiles.length; i++) {
                    formData.append('review_files', editReview.newFiles[i]);
                    if (editReview.newFiles[i].type.startsWith('image/')) {
                        isImage = true;
                    }
                    if (editReview.newFiles[i].type.startsWith('video/')) {
                        isVideo = true;
                    }
                }
                
                // Determine review type based on media type
                let reviewType = editReview.review_type; // Keep existing by default
                
                // Only update review type if there are new files
                if (isImage && isVideo) {
                    reviewType = 3; // for both image and video
                } else if (isImage) {
                    reviewType = 1; // for image review
                } else if (isVideo) {
                    reviewType = 2; // for video review
                }
                
                formData.append('review_type', reviewType);
            }
            
            // Make the API call
            const response = await api.postWithToken(`${serverURL}/api/product-review-manage`, formData);
            
            if (response?.data.success === true) {
                handleSuccessSnackbar("Review updated successfully");
                setShowEditModal(false);
                getProductReview(); // Refresh the review list
            } else {
                handleErrorSnackbar(response?.data.message || "Failed to update review");
            }
        } catch (error) {
            console.log(error);
            handleErrorSnackbar("An error occurred. Please try again.");
        } finally {
            setMainLoder(false);
        }
    };

    // Snackbar handlers
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSuccessSnackbarOpen(false);
        setErrorSnackbarOpen(false);
    };

    const handleSuccessSnackbar = (message) => {
        setSnackbarMessage(message || 'Operation successful');
        setSuccessSnackbarOpen(true);
        setTimeout(() => {
            setSuccessSnackbarOpen(false);
        }, 3000);
    };

    const handleErrorSnackbar = (errorMessage) => {
        setSnackbarMessage(errorMessage || 'An error occurred');
        setErrorSnackbarOpen(true);
        setTimeout(() => {
            setErrorSnackbarOpen(false);
        }, 3000);
    };

    if (loading) {
        return (
            <Layout>
                <div className="border-green">
                    <div className="p-3 p-sm-4">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading reviews...</p>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="border-green">
                <div className="p-3 p-sm-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3>Product Reviews</h3>
                        <Button 
                            variant="secondary" 
                            onClick={() => navigate('/admin/product')}
                            className="d-flex align-items-center gap-2"
                        >
                            ← Back to Products
                        </Button>
                    </div>
                    {reviewList?.list?.length > 0 ? (
                        <React.Fragment>
                            <Table responsive bordered className="mb-0">
                        <thead>
                            <tr>
                                <th width="3%">No.</th>
                                <th width="12%">Review Image</th>
                                <th width="15%">User Name</th>
                                <th width="15%">Rating</th>
                                <th width="10%">Comment</th>
                                <th width="10%">Heading</th>
                                <th width="10%">Review Date</th>
                                <th width="9%">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                        {reviewList.list.map((review, index) => {
                            const currentNumber = (page - 1) * itemsPerPage + index + 1;
                            return (
                                <tr key={index}>
                                    <td>{currentNumber}.</td>
                                    <td width="12%">
                                        {review.review_files && review.review_files.length > 0 ? (
                                            review.review_type === 2 || review.review_files[0]?.file_name?.includes('video') || review.review_files[0]?.media_type === 'video' ? (
                                                <video style={{ width: "100px", height: "100px", objectFit: "cover" }} controls>
                                                    <source src={review.review_files[0].file_name} type="video/mp4" />
                                                    Your browser does not support the video.
                                                </video>
                                            ) : (
                                                <img style={{ width: "100px", height: "100px", objectFit: "cover" }} src={review.review_files[0].file_name} alt='Review' />
                                            )
                                        ) : (
                                            <span>No Image Available</span>
                                        )}
                                    </td>
                                    <td className='text-pre-line'>{review.reviewer_display_name || review.source_reviewer_name || review.user_id?.name || review.title || "Shopper"}</td>
                                    <td className='text-pre-line'><Rating name="simple-controlled" value={review.rating} readOnly /></td>
                                    <td className='text-pre-line'>{review.content}</td>
                                    <td className='text-pre-line'>{review.heading}</td>
                                    <td className='text-pre-line'>{review.created_at.slice(0, 10)}</td>
                                    <td>
                                        <div className="user-tools d-flex align-items-center gap-2 justify-content-center">
                                            <Button 
                                                variant="info" 
                                                size="sm"
                                                onClick={() => navigate(`/admin/view-review/${review._id}`)}
                                                title="View Details"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                                                </svg>
                                            </Button>
                                            <Button onClick={() => handleEditClick(review)}>
                                                <img src={editicon} className='user-review-icon' alt="Edit" />
                                            </Button>
                                            <Button onClick={() => deleteReview(review._id)}>
                                                <img src={deletereview} className='user-review-icon' alt="Delete" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                    {reviewList?.totalPages > 1 &&
                        <DynamicPagination
                            currentPage={reviewList.currentPage}
                            totalPages={reviewList.totalPages}
                            onPageChange={(newPage) => {
                                setPage(newPage);
                            }}
                        />
                    }
                    
                    {/* Edit Review Modal */}
                    <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Review</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={submitEditedReview}>
                                <Row>
                                    <Col lg={6} md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Reviewer display name*</Form.Label>
                                            <Form.Control 
                                                type="text" 
                                                name="reviewer_display_name"
                                                value={editReview.reviewer_display_name}
                                                onChange={handleInputChange}
                                                placeholder="Enter reviewer display name"
                                            />
                                            {editReview.source_reviewer_name && (
                                                <div className="d-flex align-items-center justify-content-between gap-2 mt-2">
                                                    <Form.Text>
                                                        Source name: {editReview.source_reviewer_name}
                                                    </Form.Text>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        onClick={() => setEditReview(prev => ({
                                                            ...prev,
                                                            reviewer_display_name: prev.source_reviewer_name
                                                        }))}
                                                    >
                                                        Restore source name
                                                    </Button>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col lg={6} md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Title*</Form.Label>
                                            <Form.Control 
                                                type="text" 
                                                name="heading"
                                                value={editReview.heading} 
                                                onChange={handleInputChange}
                                                placeholder="Enter review title"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Rating*</Form.Label>
                                    <div>
                                        <Rating 
                                            name="rating"
                                            value={editReview.rating} 
                                            onChange={handleRatingChange}
                                            precision={0.5}
                                            className="rating"
                                        />
                                    </div>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Comments*</Form.Label>
                                    <Form.Control 
                                        as="textarea" 
                                        rows={3} 
                                        name="content"
                                        value={editReview.content} 
                                        onChange={handleInputChange}
                                        placeholder="Enter your review comments"
                                    />
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Current Review Media</Form.Label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {editReview.review_files && editReview.review_files.length > 0 ? (
                                            editReview.review_files.map((file, idx) => {
                                                const isMarkedForRemoval = filesToRemove.includes(file._id);
                                                return (
                                                    <div key={idx} className="position-relative" style={{ opacity: isMarkedForRemoval ? 0.5 : 1 }}>
                                                        {file.file_name.endsWith('.mp4') || file.file_name.includes('video') ? (
                                                            <video style={{ width: "100px", height: "100px", objectFit: "cover" }} controls>
                                                                <source src={file.file_name} type="video/mp4" />
                                                                Your browser does not support the video.
                                                            </video>
                                                        ) : (
                                                            <img style={{ width: "100px", height: "100px", objectFit: "cover" }} src={file.file_name} alt='' />
                                                        )}
                                                        <Button 
                                                            variant={isMarkedForRemoval ? "success" : "danger"}
                                                            size="sm"
                                                            style={{ position: "absolute", top: "5px", right: "5px", padding: "2px 5px" }}
                                                            onClick={() => isMarkedForRemoval ? handleRestoreFile(file._id) : handleRemoveExistingFile(file._id)}
                                                        >
                                                            {isMarkedForRemoval ? "Restore" : "X"}
                                                        </Button>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <span>No media files available</span>
                                        )}
                                    </div>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Upload New Files (Max 5)</Form.Label>
                                    <Form.Control 
                                        type="file" 
                                        name="review_files"
                                        accept="image/*,video/*"
                                        onChange={handlePhoto}
                                        multiple
                                    />
                                    <small className="text-muted">
                                        Supported formats: Images and Videos
                                    </small>
                                </Form.Group>
                                
                                {editReview.fileNames && editReview.fileNames.length > 0 && (
                                    <div className="mb-3">
                                        <strong>Selected Files:</strong>
                                        <ul>
                                            {editReview.fileNames.map((fileName, index) => (
                                                <li key={index}>{fileName}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                <div className="d-flex justify-content-end gap-2">
                                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="submit">
                                        Save Changes
                                    </Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>
                        </React.Fragment>
                    ) : (
                        <div className="text-center py-5">
                            <p className="mb-3">No reviews found for this product.</p>
                            <Button variant="secondary" onClick={() => navigate(-1)}>
                                Back to Products
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Snackbar for success/error messages */}
            <Snackbar 
                open={successSnackbarOpen} 
                autoHideDuration={3000} 
                onClose={handleSnackbarClose} 
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MuiAlert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
            <Snackbar 
                open={errorSnackbarOpen} 
                autoHideDuration={3000} 
                onClose={handleSnackbarClose} 
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MuiAlert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>
        </Layout>
    );
};

export default ListUserReview;